/**
 * InventoryPage — iter150AZ.100
 *
 * Three-tab inventory workspace gated by the ``inventory.access``
 * permission (or super-admin).
 *
 *   Tab 1: Parts          — `Parts` table only.
 *   Tab 2: Warehouses     — full CRUD on `UsAirLocations`.
 *   Tab 3: Transfers      — split view: parts picker on the left,
 *                           per-part transfer history on the right.
 *                           Auto-picks the first part on mount so
 *                           the right pane never feels empty.
 *
 * iter150AZ.100 — Tab-level ErrorBoundary added so a render crash in
 * one tab can't blank out the entire InventoryPage (the symptom the
 * user reported in the screen recording — switching tabs eventually
 * produced a blank screen with no header). The boundary catches the
 * error and renders an inline retry card instead of unmounting the
 * whole tree.
 */
import { Component, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Buildings, Package, ArrowsLeftRight, Warning,
  Plus, Trash, MagnifyingGlass, ArrowLeft, ArrowDown, ArrowUp,
  CircleDashed, CaretDown, CaretRight, ThumbsUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import HeaderBrand from "@/components/HeaderBrand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n";
import { reportRenderCrash } from "@/lib/errors";
import {
  fetchInventoryMe,
  fetchInventoryParts,
  fetchWarehouses, createWarehouse, updateWarehouse, deleteWarehouse,
  fetchTransfers, createTransfer,
} from "@/lib/api";


// ─── Tab-level error boundary ────────────────────────────────────
// A crash inside any single tab is contained here so the parent
// InventoryPage shell (header + tabs) stays mounted. The user can
// retry without a full page reload.
//
// iter150AZ.107 — the boundary now also fires off a structured crash
// report (full minified stack + component stack) to the operator
// client-error log via `reportRenderCrash`. The user sees a short
// ``Error ID`` they can quote when reporting the bug, plus a
// one-click "Copy details" button that puts the raw stack on the
// clipboard so support can run the production sourcemap against it.
class TabErrorBoundary extends Component {
  state = { error: null, info: null, errorId: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // Best-effort log. Don't throw out of the boundary if reporting
    // itself fails — that would loop us right back into a crash.
    let errorId = null;
    try {
      errorId = reportRenderCrash(error, info, {
        url: typeof window !== "undefined" ? window.location?.href : null,
        code: "inventory_tab_crash",
        fallback: "InventoryPage tab crash",
      });
    } catch (_) { /* ignore */ }
    console.error("InventoryPage tab crashed:", error, info);
    this.setState({ info, errorId });
  }
  reset = () => this.setState({ error: null, info: null, errorId: null });
  copyDetails = () => {
    const lines = [
      `Error ID: ${this.state.errorId || "(none)"}`,
      `Page: ${typeof window !== "undefined" ? window.location?.href : ""}`,
      `When: ${new Date().toISOString()}`,
      `Message: ${String(this.state.error?.message || this.state.error)}`,
      "",
      "--- Error stack ---",
      String(this.state.error?.stack || ""),
      "",
      "--- Component stack ---",
      String(this.state.info?.componentStack || ""),
    ].join("\n");
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(lines).then(
          () => toast.success("Error details copied — paste them to support"),
          () => toast.error("Couldn't copy to clipboard")
        );
      } else {
        // Fallback for older browsers — show in a prompt.
        window.prompt("Copy the error details below:", lines);
      }
    } catch {
      window.prompt("Copy the error details below:", lines);
    }
  };
  render() {
    if (!this.state.error) return this.props.children;
    const stack = (this.state.info && this.state.info.componentStack) || "";
    const eid = this.state.errorId;
    return (
      <div className="rounded-md border border-red-300 bg-red-50 p-4 space-y-2"
           data-testid="inventory-tab-error">
        <div className="flex items-center gap-2 text-red-800 font-semibold">
          <Warning size={16} weight="bold" /> This tab hit an error
          {eid && (
            <span className="ml-2 font-mono text-[10px] bg-red-100 border border-red-200 rounded px-1.5 py-0.5"
                  data-testid="inventory-tab-error-id">
              ID: {eid}
            </span>
          )}
        </div>
        <p className="text-xs text-red-700 font-mono break-all">
          {String(this.state.error?.message || this.state.error)}
        </p>
        {stack && (
          <details className="text-[10px] text-red-600 font-mono">
            <summary className="cursor-pointer">Component stack</summary>
            <pre className="whitespace-pre-wrap mt-1 max-h-48 overflow-auto">
              {stack.trim()}
            </pre>
          </details>
        )}
        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={this.reset}
                  data-testid="inventory-tab-error-retry">Retry</Button>
          <Button variant="outline" size="sm" onClick={this.copyDetails}
                  data-testid="inventory-tab-error-copy">
            Copy details
          </Button>
        </div>
      </div>
    );
  }
}

const TABS = [
  { key: "parts",      label: "Parts",      icon: Package },
  { key: "warehouses", label: "Warehouses", icon: Buildings },
  { key: "transfers",  label: "Transfers",  icon: ArrowsLeftRight },
];


// ─── Shared formatting helpers (iter150AZ.104) ───────────────────
//
// Canonical SKU display rule (per user spec): "<Product Name>: <Spec1>".
// If Spec1 is empty we just show the Product Name so we don't end up
// with a trailing colon. Used in both the Parts list and the Transfers
// picker so the same part is identifiable across the whole inventory
// surface.
export function fmtSku(p) {
  if (!p) return "";
  // iter150AZ.108 — the backend now returns the canonical SKU string
  // pre-formatted as ``part_number`` (per the AppSheet CONCATENATE
  // formula). Prefer it; fall back to the legacy Name : Spec1 layout
  // for any caller that still passes the older shape.
  const canonical = String(p.part_number || "").trim();
  if (canonical) return canonical;
  const base = String(p.name || "").trim();
  const spec = String(p.spec1 || "").trim();
  return spec ? `${base}: ${spec}` : (base || "(unnamed)");
}

// Format a numeric dimension/weight as a 2-decimal string with a
// thin-space thousands separator. Returns "—" for null/empty so the
// table doesn't render misleading zeros for parts whose Product_Class
// hasn't filled in the dimension yet.
function fmtNum(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2);
}

// Group an array of {class_label, …} rows by the new class_label
// field (Product_Type + " / " + Product_Class). Returns an array of
// [label, items] tuples sorted alphabetically. Empty buckets land in
// "Uncategorized" so admins can spot rows with a missing Product_Class
// reference.
export function groupByClass(items) {
  if (!Array.isArray(items)) return [];
  const groups = new Map();
  for (const it of items) {
    const cls = (String(it?.class_label || it?.product_class || "").trim()) || "Uncategorized";
    if (!groups.has(cls)) groups.set(cls, []);
    groups.get(cls).push(it);
  }
  return [...groups.entries()].sort(([a], [b]) =>
    String(a).localeCompare(String(b)));
}

// Tiny 3-language tooltip resolver. We don't want every new label to
// require a backend i18n round-trip, so this is an inline alternative
// to `t(key)` — pass {en, fr, es} literals and the active language
// wins. Falls back to English when the active language hasn't been
// provided. Plain function so callers can derive `lang` once per
// render via ``useI18n()`` and avoid any custom-hook ambiguity.
function makeTip(lang) {
  return (en, fr, es) => {
    const map = { en, fr, es };
    return map[lang] || en;
  };
}


// iter150AZ.104 — small marker icon for parts that have never been
// transacted (no rows in Inventory_Transfers). Surfaces orphaned
// catalog entries so sales can decide whether to delist or sample.
function NoTxnBadge({ tip }) {
  return (
    <span
      data-testid="part-no-txn-badge"
      title={tip}
      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold align-middle"
    >
      <CircleDashed size={11} weight="bold" />
    </span>
  );
}

export default function InventoryPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("parts");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter150AZ.98 — when a part is selected on the Parts tab, remember
  // it here so the Transfers tab can preselect it without a refetch.
  const [selectedPart, setSelectedPart] = useState(null);  // {id,name,kind}
  // iter150AZ.102 — bump this to force the body subtree to fully
  // remount (clearing any latent state in the active tab). Wired up
  // to a "Reload tab" affordance on the global error capture banner.
  const [bodyEpoch, setBodyEpoch] = useState(0);
  // iter150AZ.102 — surface any uncaught window error or promise
  // rejection visually instead of leaving a silent blank tab (the
  // user keeps hitting blank screens on the 3rd tab switch — this
  // turns whatever is throwing into a visible message right at the
  // top of the page).
  const [globalErr, setGlobalErr] = useState("");

  useEffect(() => {
    fetchInventoryMe()
      .then(setMe)
      .catch(() => setMe({ has_access: false }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onErr = (e) => {
      const msg = (e?.error?.message || e?.message || "Unknown error").slice(0, 240);
      setGlobalErr(`${e?.error?.name || "Error"}: ${msg}`);
      console.error("[InventoryPage] window error captured:", e?.error || e);
    };
    const onRej = (e) => {
      const msg = (e?.reason?.message || String(e?.reason || "")).slice(0, 240);
      // Don't toast network 401/403/etc. — those are handled by axios.
      if (msg && !/Request failed|Network Error/i.test(msg)) {
        setGlobalErr(`Unhandled promise rejection: ${msg}`);
        console.error("[InventoryPage] unhandledrejection:", e?.reason);
      }
    };
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-500"
           data-testid="inventory-loading">
        Loading inventory access…
      </div>
    );
  }

  if (!me?.has_access) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center"
           data-testid="inventory-no-access">
        <div className="bg-white border border-slate-200 rounded-md p-6 max-w-md text-center">
          <h1 className="text-lg font-bold text-slate-900">Inventory access required</h1>
          <p className="text-sm text-slate-600 mt-2">
            Ask a Super-Admin to grant you the <code className="px-1 py-0.5 bg-slate-100 rounded">inventory.access</code> permission
            on the <a href="/admin/access" className="text-blue-700 underline">Rights matrix</a>.
          </p>
          <Button onClick={() => navigate("/")} className="mt-4"
                  data-testid="inventory-no-access-back">Back to home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="inventory-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <HeaderBrand user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
          <Button variant="outline" size="sm"
                  onClick={() => navigate("/")}
                  data-testid="inventory-back-home">
            <ArrowLeft size={14} className="mr-1" /> Home
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-black tracking-tight text-slate-900 mb-1">
          Inventory
        </h1>
        <p className="text-sm text-slate-500 mb-4">
          Parts (incl. unassigned serial-numbered equipment), warehouse
          locations, and inventory transfers.
        </p>

        {/* Tab nav */}
        <div className="border-b border-slate-200 mb-4 flex gap-1 overflow-x-auto"
             data-testid="inventory-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`inventory-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap border-b-2 transition-colors
                  ${active
                    ? "border-blue-700 text-blue-700 font-semibold"
                    : "border-transparent text-slate-600 hover:text-slate-900"}`}
              >
                <Icon size={16} weight={active ? "fill" : "regular"} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Global error capture banner — visible whenever an
            uncaught error / promise rejection has surfaced. Stays
            on screen until the user dismisses it. */}
        {globalErr && (
          <div className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 flex items-start gap-2"
               data-testid="inventory-global-error">
            <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
            <div className="flex-1 font-mono break-all">{globalErr}</div>
            <button onClick={() => { setGlobalErr(""); setBodyEpoch((n) => n + 1); }}
                    data-testid="inventory-global-error-dismiss"
                    className="text-red-700 underline shrink-0">
              Reload tab
            </button>
          </div>
        )}

        {/* iter150AZ.102 — outer boundary catches anything the per-tab
            boundary missed (e.g. an error in the rare path between
            tab switches). ``key`` on the child forces a clean remount
            of the entire tab body when bodyEpoch bumps. */}
        <TabErrorBoundary key={bodyEpoch}>
          {tab === "parts"      && (
            <TabErrorBoundary>
              <PartsTab
                onSelect={(p) => { setSelectedPart(p); setTab("transfers"); }}
              />
            </TabErrorBoundary>
          )}
          {tab === "warehouses" && (
            <TabErrorBoundary><WarehousesTab /></TabErrorBoundary>
          )}
          {tab === "transfers"  && (
            <TabErrorBoundary>
              <TransfersTab
                initialPart={selectedPart}
                onSelectionChange={setSelectedPart}
              />
            </TabErrorBoundary>
          )}
        </TabErrorBoundary>
      </main>
    </div>
  );
}


// ─── Tab 1: Parts ─────────────────────────────────────────────────
//
// iter150AZ.108 — Parts list rewritten to mirror the AppSheet view:
//   * 9 columns:
//       Part Number | IN | OUT | On Hold | In Stock |
//       Width (Inches) | Length (Inches) | Height (Inches) | Weight (lbs)
//   * IN / OUT / On Hold come from server-side aggregates over
//     Inventory_Transfers (formulas in inventory_routes.py).
//   * In Stock = MAX(0, IN − OUT − Hold), with a thumbs-up indicator
//     when positive (matches the AppSheet visual cue).
//   * Width / Length / Height / Weight are joined from Product_Class.
//   * Groups by Product_Type / Product_Class label, collapsible.
//   * Search box kept — useful at scale; default scope is the full
//     catalog (include_non_pricelist defaults true server-side).
function PartsTab({ onSelect }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  // Per-class collapse state (default: all open).
  const [collapsed, setCollapsed] = useState({});
  // iter150AZ.107 — bump to force a manual reload.
  const [reloadTick, setReloadTick] = useState(0);
  // iter150AZ.129 — "all" shows every part (including zero-stock,
  // dimmed for scanability); "available" hides anything where
  // in_stock <= 0. Defaults to "all" because the AppSheet view
  // shows everything and reviewers want a single click toggle.
  const [stockFilter, setStockFilter] = useState("all"); // "all" | "available"
  const { lang } = useI18n();
  const tip = useMemo(() => makeTip(lang), [lang]);

  // iter150AZ.107 — closure-bound `cancelled` flag replaces the old
  // mountedRef pattern (see refactor notes in iter150AZ.107).
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    // include_non_pricelist=true to mirror the AppSheet "all parts"
    // default per iter150AZ.108. Backend already defaults to true,
    // but pass explicitly so the contract is visible at the call site.
    fetchInventoryParts({ q, include_non_pricelist: true })
      .then((d) => {
        if (cancelled) return;
        setRows(Array.isArray(d?.parts) ? d.parts : []);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e?.response?.data?.detail || "Failed to load parts");
      })
      .finally(() => {
        if (cancelled) return;
        setBusy(false);
      });
    return () => { cancelled = true; };
  }, [q, reloadTick]);

  const load = useCallback(() => setReloadTick((n) => n + 1), []);

  // iter150AZ.129 — apply the "available only" toggle in-memory so
  // toggling doesn't re-fetch from the backend (search results are
  // already loaded; this is a pure UI projection).
  const visibleRows = useMemo(() => {
    if (stockFilter !== "available") return rows;
    return rows.filter(p => (Number(p.in_stock) || 0) > 0);
  }, [rows, stockFilter]);

  const groups = useMemo(() => groupByClass(visibleRows), [visibleRows]);

  // Column-count constant — used by colSpan on group header rows and
  // empty-state row so adjusting the layout only touches one place.
  const COL_COUNT = 9;

  return (
    <div data-testid="inventory-parts-tab">
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs text-slate-500"
                 title={tip(
                   "Search Product Name, Class, or Spec1",
                   "Rechercher le nom du produit, la classe ou Spec1",
                   "Buscar nombre de producto, clase o Spec1")}>
            {tip("Search by name, class or spec",
                 "Rechercher par nom, classe ou spec",
                 "Buscar por nombre, clase o spec")}
          </Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="inventory-parts-search"
              placeholder={tip(
                "filter, kit, ARO, 1/2 NPT…",
                "filtre, kit, ARO, 1/2 NPT…",
                "filtro, kit, ARO, 1/2 NPT…")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-7 h-9 text-sm"
            />
          </div>
        </div>
        <Button size="sm" onClick={load} disabled={busy}
                data-testid="inventory-parts-search-btn">
          {busy ? "Loading…" : tip("Search", "Rechercher", "Buscar")}
        </Button>
        {/* iter150AZ.129 — stock-availability toggle. Pill-style
            segmented control so the active state is obvious without
            needing a separate label. */}
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden"
              data-testid="inventory-parts-stock-filter">
          <button
            type="button"
            data-testid="inventory-parts-filter-all"
            onClick={() => setStockFilter("all")}
            className={"text-xs px-3 h-9 font-semibold transition-colors " + (
              stockFilter === "all"
                ? "bg-blue-600 text-white"
                : "bg-white text-slate-600 hover:bg-slate-50"
            )}
            title={tip(
              "Show every part (zero-stock rows are dimmed)",
              "Afficher toutes les pièces (lignes à stock zéro grisées)",
              "Mostrar todas las piezas (filas con cero stock atenuadas)")}
          >
            {tip("Show all", "Tout afficher", "Mostrar todo")}
          </button>
          <button
            type="button"
            data-testid="inventory-parts-filter-available"
            onClick={() => setStockFilter("available")}
            className={"text-xs px-3 h-9 font-semibold border-l border-slate-300 transition-colors " + (
              stockFilter === "available"
                ? "bg-blue-600 text-white"
                : "bg-white text-slate-600 hover:bg-slate-50"
            )}
            title={tip(
              "Show only parts currently in stock (In Stock > 0)",
              "Afficher uniquement les pièces en stock (En stock > 0)",
              "Mostrar solo piezas en stock (En stock > 0)")}
          >
            {tip("Available only", "Disponibles uniquement", "Solo disponibles")}
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-400 mb-2">
        {visibleRows.length} part{visibleRows.length === 1 ? "" : "s"} ·{" "}
        {groups.length} class{groups.length === 1 ? "" : "es"}
        {stockFilter === "available" && rows.length > visibleRows.length && (
          <span className="text-slate-400">
            {" "}· {rows.length - visibleRows.length} {tip(
              "zero-stock hidden",
              "à stock zéro masquées",
              "con cero stock ocultas")}
          </span>
        )}
        {" · "}{tip("click any row to view its transfer history",
                   "cliquez sur une ligne pour voir l'historique des transferts",
                   "haga clic en una fila para ver el historial de transferencias")}
      </p>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left"
                  title={tip(
                    "Canonical SKU — Product Name : Spec1 (+ Spec_3 when not a Compressor-Final, Air Dryer, Pre/Post Filter)",
                    "SKU canonique — Nom du produit : Spec1 (+ Spec_3 si non Compresseur-Final/Sécheur/Filtre)",
                    "SKU canónico — Nombre : Spec1 (+ Spec_3 cuando no es Compresor-Final/Secador/Filtro)")}>
                {tip("Part Number", "Nº de pièce", "Nº de parte")}
              </th>
              <th className="px-3 py-2 text-right"
                  title={tip(
                    "Total quantity IN (Direction=IN, excluding 'From a Warehouse')",
                    "Quantité totale entrée (Direction=IN, hors 'From a Warehouse')",
                    "Cantidad total IN (Direction=IN, excepto 'From a Warehouse')")}>
                {tip("IN", "IN", "IN")}
              </th>
              <th className="px-3 py-2 text-right"
                  title={tip(
                    "Total quantity OUT (Direction=OUT, excl. 'To Warehouse' unless Decompose)",
                    "Quantité totale sortie (Direction=OUT, hors 'To Warehouse' sauf Decompose)",
                    "Cantidad total OUT (Direction=OUT, excepto 'To Warehouse' salvo Decompose)")}>
                {tip("OUT", "OUT", "OUT")}
              </th>
              <th className="px-3 py-2 text-right"
                  title={tip(
                    "Quantity moved to a warehouse but not yet transported / received",
                    "Quantité envoyée à un entrepôt mais non encore transportée / reçue",
                    "Cantidad enviada a un almacén pero aún no transportada / recibida")}>
                {tip("On Hold", "En attente", "En espera")}
              </th>
              <th className="px-3 py-2 text-right"
                  title={tip(
                    "Available stock = MAX(0, IN − OUT − On Hold)",
                    "Stock disponible = MAX(0, IN − OUT − En attente)",
                    "Stock disponible = MAX(0, IN − OUT − En espera)")}>
                {tip("In Stock", "En stock", "En stock")}
              </th>
              <th className="px-3 py-2 text-right"
                  title={tip(
                    "Packaging width in inches (Parts row, falls back to Product_Class)",
                    "Largeur d'emballage en pouces (ligne Parts, repli sur Product_Class)",
                    "Ancho de embalaje en pulgadas (fila Parts, recurre a Product_Class)")}>
                {tip("Width (Inches)", "Largeur (po)", "Ancho (pulg)")}
              </th>
              <th className="px-3 py-2 text-right"
                  title={tip(
                    "Packaging length in inches (Parts row, falls back to Product_Class)",
                    "Longueur d'emballage en pouces (ligne Parts, repli sur Product_Class)",
                    "Largo de embalaje en pulgadas (fila Parts, recurre a Product_Class)")}>
                {tip("Length (Inches)", "Longueur (po)", "Largo (pulg)")}
              </th>
              <th className="px-3 py-2 text-right"
                  title={tip(
                    "Packaging height in inches (Parts row, falls back to Product_Class)",
                    "Hauteur d'emballage en pouces (ligne Parts, repli sur Product_Class)",
                    "Alto de embalaje en pulgadas (fila Parts, recurre a Product_Class)")}>
                {tip("Height (Inches)", "Hauteur (po)", "Alto (pulg)")}
              </th>
              <th className="px-3 py-2 text-right"
                  title={tip(
                    "Packaging weight in pounds (Parts row, falls back to Product_Class)",
                    "Poids d'emballage en livres (ligne Parts, repli sur Product_Class)",
                    "Peso de embalaje en libras (fila Parts, recurre a Product_Class)")}>
                {tip("Weight (lbs)", "Poids (lb)", "Peso (lb)")}
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr><td colSpan={COL_COUNT} className="px-3 py-6 text-center text-slate-400">
                {busy ? "Loading…" : tip("No parts match.",
                                          "Aucune pièce correspondante.",
                                          "Sin coincidencias.")}
              </td></tr>
            ) : groups.flatMap(([cls, items]) => {
              const isOpen = !collapsed[cls];
              const header = (
                <tr key={`hdr:${cls}`} className="bg-slate-100 border-t border-slate-200">
                  <td colSpan={COL_COUNT} className="px-2 py-1">
                    <button
                      onClick={() => setCollapsed((c) => ({ ...c, [cls]: !c[cls] }))}
                      className="flex items-center gap-1.5 text-xs font-bold text-slate-700 uppercase tracking-wider w-full text-left"
                      data-testid={`inventory-parts-group-${cls}`}
                    >
                      {isOpen ? <CaretDown size={12} weight="bold" />
                              : <CaretRight size={12} weight="bold" />}
                      {cls}
                      <span className="font-normal normal-case text-slate-400 ml-1">
                        ({items.length})
                      </span>
                    </button>
                  </td>
                </tr>
              );
              if (!isOpen) return [header];
              return [header, ...items.map((p, idx) => {
                const inQty   = Number(p.in_qty)   || 0;
                const outQty  = Number(p.out_qty)  || 0;
                const holdQty = Number(p.hold_qty) || 0;
                const inStock = Number(p.in_stock) || 0;
                // iter150AZ.129 — fade zero-stock rows so the
                // operator's eye lands on the stocked items first.
                // Click target / hover affordance preserved.
                const isZeroStock = inStock <= 0;
                return (
                  // iter150AZ.129 — composite key (class:idx:id)
                  // because the backend can return duplicate
                  // `_ComputedKey` values across Parts rows (e.g.
                  // the same variant listed under multiple product
                  // classes). With a non-unique key React reuses the
                  // wrong DOM node when the filter list toggles and
                  // stale `data-zero-stock` attributes leak through.
                  <tr key={`row:${cls}:${idx}:${p.id}`}
                      onClick={() => onSelect && onSelect({
                        id: p.id, name: p.name, spec1: p.spec1,
                        part_number: p.part_number,
                        product_class: p.product_class,
                        class_label: p.class_label,
                        kind: "part",
                      })}
                      className={"border-t border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors "
                                  + (isZeroStock ? "opacity-50 hover:opacity-90" : "")}
                      data-testid="inventory-part-row"
                      data-zero-stock={isZeroStock ? "true" : "false"}>
                    <td className="px-3 py-2 font-medium text-slate-900">
                      <span className="inline-flex items-center gap-1.5">
                        {fmtSku(p)}
                        {p.has_transactions === false && (
                          <NoTxnBadge tip={tip(
                            "No transactions recorded for this part yet",
                            "Aucune transaction enregistrée pour cette pièce",
                            "Sin transacciones registradas para esta pieza")} />
                        )}
                      </span>
                    </td>
                    {/* IN — green ↓ */}
                    <td className="px-3 py-2 text-right font-mono"
                        data-testid="inventory-part-in">
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <ArrowDown size={11} weight="bold" />
                        {fmtNum(inQty)}
                      </span>
                    </td>
                    {/* OUT — red ↑ */}
                    <td className="px-3 py-2 text-right font-mono"
                        data-testid="inventory-part-out">
                      <span className="inline-flex items-center gap-1 text-red-700">
                        <ArrowUp size={11} weight="bold" />
                        {fmtNum(outQty)}
                      </span>
                    </td>
                    {/* On Hold */}
                    <td className="px-3 py-2 text-right font-mono text-slate-600"
                        data-testid="inventory-part-hold">
                      {fmtNum(holdQty)}
                    </td>
                    {/* In Stock — thumbs-up cue when positive */}
                    <td className="px-3 py-2 text-right font-mono"
                        data-testid="inventory-part-in-stock">
                      {inStock > 0 ? (
                        <span className="inline-flex items-center gap-1 font-bold text-slate-900">
                          <ThumbsUp size={11} weight="bold" className="text-green-600" />
                          {fmtNum(inStock)}
                        </span>
                      ) : (
                        <span className="text-slate-400">0.00</span>
                      )}
                    </td>
                    {/* Dimensions */}
                    <td className="px-3 py-2 text-right text-slate-600 font-mono">{fmtNum(p.width)}</td>
                    <td className="px-3 py-2 text-right text-slate-600 font-mono">{fmtNum(p.length)}</td>
                    <td className="px-3 py-2 text-right text-slate-600 font-mono">{fmtNum(p.height)}</td>
                    <td className="px-3 py-2 text-right text-slate-600 font-mono">{fmtNum(p.weight)}</td>
                  </tr>
                );
              })];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── Tab 2: Warehouses (usarlocations CRUD) ───────────────────────
function WarehousesTab() {
  const [rows, setRows] = useState([]);
  const [diag, setDiag] = useState(null);   // iter150AZ.96 — schema diagnostics from backend
  const [editing, setEditing] = useState(null);   // null = new, or row obj
  const [form, setForm] = useState({ name: "", address: "", contact: "", active: true });
  const [busy, setBusy] = useState(false);
  // iter150AZ.107 — reload tick instead of mountedRef. Bumping this
  // re-runs the load effect with a fresh `cancelled` flag closure so
  // stale in-flight responses can never call setState on an unmounted
  // tab.
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    fetchWarehouses()
      .then((d) => {
        if (cancelled) return;
        setRows(Array.isArray(d?.warehouses) ? d.warehouses : []);
        setDiag(d?._diagnostics || null);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e?.response?.data?.detail || "Failed to load warehouses");
      });
    return () => { cancelled = true; };
  }, [reloadTick]);

  const startNew = () => {
    setEditing("new");
    setForm({ name: "", address: "", contact: "", active: true });
  };

  const startEdit = (w) => {
    setEditing(w);
    setForm({
      name:    w.name || "",
      address: w.address || "",
      contact: w.contact || "",
      active:  w.active !== false,
    });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      if (editing === "new") {
        await createWarehouse(form);
        toast.success(`${form.name} created`);
      } else {
        await updateWarehouse(editing.id, form);
        toast.success(`${form.name} updated`);
      }
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete warehouse "${w.name}"?`)) return;
    try {
      await deleteWarehouse(w.id);
      toast.success(`${w.name} deleted`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="inventory-warehouses-tab">
      {/* iter150AZ.96 — Surface backend schema-discovery diagnostics
          so admins can confirm the right table + columns are being
          used in production. Only shown when no rows came back or
          the backend reported an explicit error. */}
      {diag && (rows.length === 0 || diag.error) && (
        <details
          data-testid="inventory-warehouses-diagnostics"
          className={`mb-3 rounded-md border px-3 py-2 text-xs ${
            diag.error
              ? "border-red-300 bg-red-50 text-red-800"
              : "border-amber-300 bg-amber-50 text-amber-900"
          }`}
        >
          <summary className="cursor-pointer font-semibold">
            {diag.error ? "Schema error — click for details" : "No warehouses found — schema diagnostics"}
          </summary>
          <div className="mt-2 space-y-1 font-mono">
            {diag.error && <div><b>Error:</b> {diag.error}</div>}
            {diag.table && <div><b>Table:</b> {diag.table}</div>}
            {diag.picked && typeof diag.picked === "object" && !Array.isArray(diag.picked) && (
              <div>
                <b>Picked columns:</b>{" "}
                {Object.entries(diag.picked)
                  .filter(([, v]) => v)
                  .map(([k, v]) => `${k}=${v}`)
                  .join(", ") || "(none)"}
              </div>
            )}
            {Array.isArray(diag.all_columns) && (
              <div className="break-words">
                <b>All columns on table:</b> {diag.all_columns.join(", ")}
              </div>
            )}
            {Array.isArray(diag.probed_tables) && (
              <div><b>Probed tables:</b> {diag.probed_tables.join(", ")}</div>
            )}
          </div>
        </details>
      )}

      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">
          {rows.length} warehouse{rows.length === 1 ? "" : "s"}.
        </p>
        <Button size="sm" onClick={startNew}
                data-testid="inventory-warehouse-add-btn">
          <Plus size={14} className="mr-1" /> New warehouse
        </Button>
      </div>

      {editing && (
        <div className="bg-white border border-blue-200 rounded-md p-4 mb-4 space-y-2"
             data-testid="inventory-warehouse-form">
          <h3 className="text-sm font-semibold text-slate-800">
            {editing === "new" ? "New warehouse" : `Edit ${editing.name}`}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="inventory-warehouse-form-name" />
            </div>
            <div>
              <Label className="text-xs">Contact</Label>
              <Input value={form.contact}
                     onChange={(e) => setForm({ ...form, contact: e.target.value })}
                     data-testid="inventory-warehouse-form-contact" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Address</Label>
              <Input value={form.address}
                     onChange={(e) => setForm({ ...form, address: e.target.value })}
                     data-testid="inventory-warehouse-form-address" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active}
                     onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Active
            </label>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}
                    data-testid="inventory-warehouse-form-cancel">Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy}
                    data-testid="inventory-warehouse-form-save">
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Address</th>
              <th className="px-3 py-2 text-left">Contact</th>
              <th className="px-3 py-2 text-right" />
            </tr>
          </thead>
          <tbody>
            {(() => {
              // iter150AZ.131 — Group by active state, active first.
              // Inactive rows are dimmed so the eye lands on the
              // operational warehouses. The dedicated ACTIVE column
              // is gone — the group header carries that information.
              if (!Array.isArray(rows) || rows.length === 0) {
                return (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">
                    No warehouses yet. Click &quot;New warehouse&quot; to add the first.
                  </td></tr>
                );
              }
              const active = rows.filter(w => w?.active !== false);
              const inactive = rows.filter(w => w?.active === false);

              const renderRow = (w, dimmed) => (
                <tr key={w?.id ?? Math.random()}
                    className={"border-t border-slate-100 transition-colors "
                                 + (dimmed
                                       ? "opacity-50 hover:opacity-90 bg-slate-50/50"
                                       : "hover:bg-slate-50")}
                    data-testid={`inventory-warehouse-row-${w?.id ?? ""}`}
                    data-active={(w?.active !== false) ? "true" : "false"}>
                  <td className="px-3 py-2 font-medium">{w?.name || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{w?.address || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{w?.contact || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(w)}
                            data-testid={`inventory-warehouse-edit-${w.id}`}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(w)}
                            data-testid={`inventory-warehouse-delete-${w.id}`}>
                      <Trash size={13} className="text-red-500" />
                    </Button>
                  </td>
                </tr>
              );

              const out = [];
              if (active.length > 0) {
                out.push(
                  <tr key="hdr-active" className="bg-emerald-50 border-t border-emerald-200"
                      data-testid="inventory-warehouse-group-active">
                    <td colSpan={4} className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-emerald-800">
                      Active · {active.length}
                    </td>
                  </tr>
                );
                active.forEach(w => out.push(renderRow(w, false)));
              }
              if (inactive.length > 0) {
                out.push(
                  <tr key="hdr-inactive" className="bg-slate-100 border-t border-slate-200"
                      data-testid="inventory-warehouse-group-inactive">
                    <td colSpan={4} className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                      Inactive · {inactive.length}
                    </td>
                  </tr>
                );
                inactive.forEach(w => out.push(renderRow(w, true)));
              }
              return out;
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── Tab 3: Transfers — split view (parts list + per-part journal) ─
//
// iter150AZ.99 — Per user spec:
//   * the picker shows ONLY parts from the `Parts` table (no serials)
//   * the transfer detail surfaces the serial number (via JOIN on
//     `Inventory_Transfers.SerialID` → `Products_By_Serial`)
//   * search fields filter independently for serial, handled-by, and
//     SourceID, all within the chosen part.
function TransfersTab({ initialPart, onSelectionChange }) {
  const { lang } = useI18n();
  const tip = useMemo(() => makeTip(lang), [lang]);
  // ── Left pane: parts picker ─────────────────────────────────
  const [pickerQ, setPickerQ] = useState("");
  const [parts, setParts] = useState([]);
  const [pickerBusy, setPickerBusy] = useState(false);
  // iter150AZ.104 — multi-select. ``picked`` is a Map<id, partRecord>
  // so we can render selected labels in the right pane and POST
  // ``part_ids=A,B,C`` to the transfers endpoint. ``initialPart``
  // (legacy single-select carryover from the Parts tab) seeds the
  // map so existing nav from Parts → Transfers still primes the view.
  const [pickedMap, setPickedMap] = useState(() => {
    const m = new Map();
    if (initialPart?.id) m.set(initialPart.id, initialPart);
    return m;
  });
  const [collapsedClasses, setCollapsedClasses] = useState({});

  // ── Right pane: filters + transfers ─────────────────────────
  const [rows, setRows] = useState([]);
  const [rowsBusy, setRowsBusy] = useState(false);
  const [rowsErr, setRowsErr] = useState("");
  const [diag, setDiag] = useState(null);
  // Independent filters, each AND'd on the server. The committed
  // filters (applied) drive the request; the editable form below
  // captures keystrokes without thrashing the backend.
  const [filters, setFilters] = useState(
    { serial: "", handled_by: "", source_id: "" });
  const [filterForm, setFilterForm] = useState(
    { serial: "", handled_by: "", source_id: "" });
  const [warehouses, setWarehouses] = useState([]);

  // ── Create-transfer form (still supported) ──────────────────
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    qty: 1,
    source_type: "warehouse",      source_id: "", source_label: "",
    destination_type: "warehouse", destination_id: "", destination_label: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  // iter150AZ.107 — bump to refetch the transfers list after a
  // successful create. Avoids needing a named callback for the
  // pure-effect refactor of the transfers fetch.
  const [transfersReloadTick, setTransfersReloadTick] = useState(0);

  // iter150AZ.107 — reload tick replaces mountedRef. Bumping this
  // re-runs the picker effect with a fresh `cancelled` flag closure.
  const [pickerReloadTick, setPickerReloadTick] = useState(0);
  const reloadPicker = useCallback(
    () => setPickerReloadTick((n) => n + 1), []);

  // Load parts whenever the picker query changes or the user manually
  // requests a reload. Auto-pick the first row when nothing is
  // selected yet so the right pane never feels empty.
  useEffect(() => {
    let cancelled = false;
    setPickerBusy(true);
    fetchInventoryParts({ q: pickerQ, include_non_pricelist: true })
      .then((d) => {
        if (cancelled) return;
        const list = Array.isArray(d?.parts) ? d.parts : [];
        setParts(list);
        // Auto-pick only when nothing is picked — uses functional
        // setState so we read the *current* pickedMap inside the
        // updater, sidestepping the stale-closure trap.
        if (list.length > 0) {
          setPickedMap((cur) => {
            if (cur.size > 0) return cur;
            const first = list[0];
            const sel = { id: first.id, name: first.name, spec1: first.spec1,
                           part_number: first.part_number,
                           product_class: first.product_class,
                           class_label: first.class_label, kind: "part" };
            try { onSelectionChange && onSelectionChange(sel); } catch (_) { /* ignore */ }
            return new Map([[sel.id, sel]]);
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setParts([]);
      })
      .finally(() => {
        if (cancelled) return;
        setPickerBusy(false);
      });
    return () => { cancelled = true; };
  }, [pickerQ, pickerReloadTick, onSelectionChange]);

  useEffect(() => {
    let cancelled = false;
    fetchWarehouses()
      .then((d) => {
        if (cancelled) return;
        setWarehouses(Array.isArray(d?.warehouses) ? d.warehouses : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Group the picker rows by ``product_class`` so the user can
  // collapse classes they don't care about.
  const pickerGroups = useMemo(
    () => groupByClass(Array.isArray(parts) ? parts : []),
    [parts]);

  // iter150AZ.107 — Load transfers as a pure effect, no callback ref
  // gymnastics. Closing over the latest pickedMap/filters and using a
  // `cancelled` flag eliminates the stale ref hazard.
  useEffect(() => {
    const ids = [...pickedMap.keys()].map(String).filter(Boolean);
    if (ids.length === 0) {
      setRows([]); setDiag(null); setRowsErr("");
      return;
    }
    let cancelled = false;
    setRowsBusy(true); setRowsErr("");
    const queryParams = ids.length > 1
      ? { part_ids: ids.join(","), limit: 1000,
          serial: filters.serial || undefined,
          handled_by: filters.handled_by || undefined,
          source_id: filters.source_id || undefined }
      : { part_id: ids[0], limit: 1000,
          serial: filters.serial || undefined,
          handled_by: filters.handled_by || undefined,
          source_id: filters.source_id || undefined };
    fetchTransfers(queryParams)
      .then((d) => {
        if (cancelled) return;
        setRows(Array.isArray(d?.transfers) ? d.transfers : []);
        setDiag(d?._diagnostics || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setRowsErr(e?.response?.data?.detail || "Failed to load transfers");
      })
      .finally(() => {
        if (cancelled) return;
        setRowsBusy(false);
      });
    return () => { cancelled = true; };
  }, [pickedMap, filters, transfersReloadTick]);

  // Toggle a part in/out of the selection. iter150AZ.104.
  const togglePick = (p) => {
    if (!p?.id) return;
    setPickedMap((cur) => {
      const next = new Map(cur);
      if (next.has(p.id)) {
        next.delete(p.id);
      } else {
        const sel = { id: p.id, name: p.name, spec1: p.spec1,
                       part_number: p.part_number,
                       product_class: p.product_class,
                       class_label: p.class_label, kind: "part" };
        next.set(p.id, sel);
        // Mirror the most recently picked part back to the parent
        // (used by Parts → Transfers nav). It's a single value, so
        // we report the last add.
        onSelectionChange && onSelectionChange(sel);
      }
      return next;
    });
    // Reset filters on a selection change so we don't accidentally
    // hide everything that just got added.
    setFilters({ serial: "", handled_by: "", source_id: "" });
    setFilterForm({ serial: "", handled_by: "", source_id: "" });
  };

  const clearAllPicks = () => {
    setPickedMap(new Map());
    setFilters({ serial: "", handled_by: "", source_id: "" });
    setFilterForm({ serial: "", handled_by: "", source_id: "" });
  };

  const applyFilters = () => setFilters({ ...filterForm });
  const clearFilters = () => {
    const empty = { serial: "", handled_by: "", source_id: "" };
    setFilterForm(empty);
    setFilters(empty);
  };
  const anyFilterActive = filters.serial || filters.handled_by || filters.source_id;

  const submitTransfer = async () => {
    // For "New transfer" we require EXACTLY one selected part — the
    // form only models a single move, so multi-select is ambiguous
    // here. The user picks a single part on the left first.
    const picksArr = [...pickedMap.values()];
    if (picksArr.length !== 1) {
      return toast.error(picksArr.length === 0
        ? "Pick a part first"
        : "Recording a transfer requires a single selected part — uncheck the others first.");
    }
    const onlyPick = picksArr[0];
    if (!form.qty || form.qty <= 0) return toast.error("Qty must be > 0");
    setSaving(true);
    try {
      await createTransfer({
        part_id: onlyPick.id,
        qty:     Number(form.qty),
        source:      { type: form.source_type, id: form.source_id,
                       label: form.source_label },
        destination: { type: form.destination_type, id: form.destination_id,
                       label: form.destination_label },
        notes: form.notes,
      });
      toast.success("Transfer recorded");
      setShowForm(false);
      setForm({
        qty: 1,
        source_type: "warehouse",      source_id: "", source_label: "",
        destination_type: "warehouse", destination_id: "", destination_label: "",
        notes: "",
      });
      setTransfersReloadTick((n) => n + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="inventory-transfers-tab"
         className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
      {/* ── Left: part picker (multi-select, grouped by class) ── */}
      <aside className="bg-white border border-slate-200 rounded-md flex flex-col min-h-[500px]"
             data-testid="inventory-transfers-picker">
        <div className="p-3 border-b border-slate-100">
          <Label className="text-xs text-slate-500"
                 title={tip(
                   "Pick one or more parts to view their combined transfer history",
                   "Choisissez une ou plusieurs pièces pour voir l'historique combiné",
                   "Elija una o más piezas para ver el historial combinado")}>
            {tip("Pick part(s) — multi-select",
                 "Choisir des pièces — sélection multiple",
                 "Elegir piezas — selección múltiple")}
          </Label>
          <div className="relative mt-1">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={pickerQ}
                   onChange={(e) => setPickerQ(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && reloadPicker()}
                   placeholder={tip(
                     "name, class, or spec…",
                     "nom, classe ou spec…",
                     "nombre, clase o spec…")}
                   className="pl-7 h-9 text-sm"
                   data-testid="inventory-transfers-picker-search" />
          </div>
          <div className="flex items-center justify-between mt-1.5 text-[10px] text-slate-400">
            <span>
              {parts.length} part{parts.length === 1 ? "" : "s"} ·
              {" "}{pickerGroups.length} class{pickerGroups.length === 1 ? "" : "es"}
              {pickerBusy && " · loading…"}
            </span>
            {pickedMap.size > 0 && (
              <button onClick={clearAllPicks}
                      className="text-blue-700 underline hover:text-blue-900"
                      data-testid="inventory-transfers-clear-picks">
                {tip(`Clear (${pickedMap.size} selected)`,
                     `Effacer (${pickedMap.size} sélectionnés)`,
                     `Borrar (${pickedMap.size} seleccionados)`)}
              </button>
            )}
          </div>
        </div>
        <div className="overflow-y-auto max-h-[560px] flex-1">
          {pickerGroups.length === 0 ? (
            <p className="p-6 text-center text-slate-400 text-sm">
              {pickerBusy ? "Loading…" : tip("Nothing matches.",
                                              "Aucune correspondance.",
                                              "Sin coincidencias.")}
            </p>
          ) : pickerGroups.map(([cls, items]) => {
            const isOpen = !collapsedClasses[cls];
            return (
              <div key={`grp:${cls}`}>
                <button
                  onClick={() => setCollapsedClasses((c) => ({ ...c, [cls]: !c[cls] }))}
                  className="w-full flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 border-y border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-700 text-left"
                  data-testid={`inventory-transfers-class-${cls}`}
                >
                  {isOpen ? <CaretDown size={11} weight="bold" />
                          : <CaretRight size={11} weight="bold" />}
                  {cls}
                  <span className="font-normal normal-case text-slate-400 ml-1">
                    ({items.length})
                  </span>
                </button>
                {isOpen && items.map((p) => {
                  const sel = pickedMap.has(p?.id);
                  return (
                    <label
                      key={p?.id || Math.random()}
                      data-testid="inventory-transfers-pick-part"
                      className={`flex items-start gap-2 px-3 py-2 border-b border-slate-100 cursor-pointer transition-colors ${
                        sel
                          ? "bg-blue-50 border-l-2 border-l-blue-700"
                          : "hover:bg-slate-50 border-l-2 border-l-transparent"
                      }`}
                    >
                      <input type="checkbox" className="mt-1"
                             checked={sel}
                             onChange={() => togglePick(p)} />
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-slate-900 truncate">
                          {fmtSku(p)}
                          {p?.has_transactions === false && (
                            <span className="ml-1 align-middle">
                              <NoTxnBadge tip={tip(
                                "No transactions yet",
                                "Aucune transaction",
                                "Sin transacciones")} />
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      </aside>

      {/* ── Right: filters + transfers ──────────────────────────── */}
      <section className="bg-white border border-slate-200 rounded-md min-h-[500px]"
               data-testid="inventory-transfers-detail">
        {pickedMap.size === 0 ? (
          <div className="p-10 text-center text-slate-400">
            <ArrowsLeftRight size={32} className="mx-auto mb-2" />
            <p className="text-sm">
              {tip("Pick one or more parts on the left to see their transfer history.",
                   "Choisissez une ou plusieurs pièces à gauche pour voir l'historique.",
                   "Elija una o más piezas a la izquierda para ver el historial.")}
            </p>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">
                  {pickedMap.size === 1
                    ? tip("Transfers for", "Transferts pour", "Transferencias para")
                    : tip(`Combined transfers for ${pickedMap.size} parts`,
                          `Transferts combinés pour ${pickedMap.size} pièces`,
                          `Transferencias combinadas para ${pickedMap.size} piezas`)}
                </p>
                <h3 className="text-base font-bold text-slate-900 truncate"
                    title={[...pickedMap.values()].map(fmtSku).join(" · ")}
                    data-testid="inventory-transfers-detail-title">
                  {pickedMap.size === 1
                    ? fmtSku([...pickedMap.values()][0])
                    : [...pickedMap.values()].slice(0, 3).map(fmtSku).join(", ")
                      + (pickedMap.size > 3 ? ` · +${pickedMap.size - 3}` : "")}
                </h3>
              </div>
              <Button size="sm" onClick={() => setShowForm((v) => !v)}
                      disabled={pickedMap.size !== 1}
                      title={pickedMap.size !== 1
                        ? tip("Select exactly one part to record a new transfer",
                              "Sélectionnez une seule pièce pour enregistrer un transfert",
                              "Seleccione una sola pieza para registrar una transferencia")
                        : ""}
                      data-testid="inventory-transfer-add-btn">
                <Plus size={14} className="mr-1" /> {tip("New transfer", "Nouveau transfert", "Nueva transferencia")}
              </Button>
            </div>

            {/* Independent filter row — each input refines the
                current part's transfer list. */}
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 grid grid-cols-1 md:grid-cols-4 gap-2 items-end"
                 data-testid="inventory-transfers-filter-bar">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500"
                       title={tip(
                         "Filter by serial number — matches both the SerialID key and the readable serial string",
                         "Filtrer par numéro de série — correspond à la clé SerialID et à la chaîne lisible",
                         "Filtrar por número de serie — coincide con la clave SerialID y la cadena legible")}>
                  {tip("Serial #", "Nº de série", "Nº de serie")}
                </Label>
                <Input value={filterForm.serial}
                       onChange={(e) => setFilterForm({ ...filterForm, serial: e.target.value })}
                       onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                       placeholder="SN-1234…"
                       className="h-8 text-sm"
                       data-testid="inventory-transfers-filter-serial" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500"
                       title={tip(
                         "Filter by the user who handled the transfer (email or name, partial match)",
                         "Filtrer par utilisateur ayant traité le transfert (courriel ou nom, correspondance partielle)",
                         "Filtrar por usuario que manejó la transferencia (correo o nombre, coincidencia parcial)")}>
                  {tip("Handled by", "Traité par", "Manejado por")}
                </Label>
                <Input value={filterForm.handled_by}
                       onChange={(e) => setFilterForm({ ...filterForm, handled_by: e.target.value })}
                       onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                       placeholder="email or name…"
                       className="h-8 text-sm"
                       data-testid="inventory-transfers-filter-handled-by" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500"
                       title={tip(
                         "Filter by the source warehouse the part was moved out of",
                         "Filtrer par entrepôt source d'où la pièce est partie",
                         "Filtrar por almacén origen del cual salió la pieza")}>
                  {tip("Source warehouse", "Entrepôt source", "Almacén origen")}
                </Label>
                <select value={filterForm.source_id}
                        onChange={(e) => setFilterForm({ ...filterForm, source_id: e.target.value })}
                        className="block w-full h-8 px-2 text-sm border border-slate-300 rounded-md bg-white"
                        data-testid="inventory-transfers-filter-source-id">
                  <option value="">Any source</option>
                  {(Array.isArray(warehouses) ? warehouses : []).map((w) => (
                    <option key={w?.id ?? Math.random()} value={w?.id ?? ""}>{w?.name || "(unnamed)"}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={applyFilters}
                        data-testid="inventory-transfers-apply-filters">
                  Apply
                </Button>
                {anyFilterActive && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}
                          data-testid="inventory-transfers-clear-filters">
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {showForm && (
              <div className="bg-blue-50/40 border-b border-blue-100 p-4 space-y-3"
                   data-testid="inventory-transfer-form">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Qty *</Label>
                    <Input type="number" value={form.qty}
                           onChange={(e) => setForm({ ...form, qty: e.target.value })}
                           data-testid="inventory-transfer-form-qty" />
                  </div>
                </div>
                <EndpointRow side="source" form={form} setForm={setForm} warehouses={warehouses} />
                <EndpointRow side="destination" form={form} setForm={setForm} warehouses={warehouses} />
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Input value={form.notes}
                         onChange={(e) => setForm({ ...form, notes: e.target.value })}
                         data-testid="inventory-transfer-form-notes" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}
                          data-testid="inventory-transfer-form-cancel">Cancel</Button>
                  <Button size="sm" onClick={submitTransfer} disabled={saving}
                          data-testid="inventory-transfer-form-save">
                    {saving ? "Saving…" : "Record transfer"}
                  </Button>
                </div>
              </div>
            )}

            {rowsErr && (
              <div className="m-3 p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded">
                {rowsErr}
              </div>
            )}
            {diag && (!Array.isArray(rows) || rows.length === 0 || diag.error) && (
              <details className={`mx-3 my-2 rounded border px-3 py-2 text-[11px] ${
                diag.error ? "border-red-300 bg-red-50 text-red-800"
                          : "border-amber-200 bg-amber-50 text-amber-900"}`}
                       data-testid="inventory-transfers-diagnostics">
                <summary className="cursor-pointer font-semibold">
                  {diag.error ? "Schema error — click for details"
                              : "No transfers found — schema diagnostics"}
                </summary>
                <div className="mt-2 space-y-1 font-mono">
                  {diag.error && <div><b>Error:</b> {diag.error}</div>}
                  {diag.table && <div><b>Table:</b> {diag.table}</div>}
                  {diag.picked && typeof diag.picked === "object" && !Array.isArray(diag.picked) && (
                    <div><b>Picked columns:</b>{" "}
                      {Object.entries(diag.picked).filter(([, v]) => v)
                        .map(([k, v]) => `${k}=${v}`).join(", ") || "(none)"}
                    </div>
                  )}
                  {Array.isArray(diag.all_columns) && (
                    <div className="break-words">
                      <b>All columns:</b> {diag.all_columns.join(", ")}
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    {/* iter150AZ.98 — ActionDate is the first column. */}
                    <th className="px-3 py-2 text-left"
                        title={tip("Production ActionDate column",
                                   "Colonne ActionDate de production",
                                   "Columna ActionDate de producción")}>
                      {tip("Action date", "Date d'action", "Fecha")}
                    </th>
                    {/* iter150AZ.104 — extra Part column visible only
                        when multiple parts are selected, so combined
                        rows are still identifiable. */}
                    {pickedMap.size > 1 && (
                      <th className="px-3 py-2 text-left"
                          title={tip("Which part this row belongs to (multi-select view)",
                                     "À quelle pièce cette ligne appartient (vue multi-sélection)",
                                     "A qué pieza pertenece esta fila (vista multi-selección)")}>
                        {tip("Part", "Pièce", "Pieza")}
                      </th>
                    )}
                    <th className="px-3 py-2 text-left"
                        title={tip("IN = into a warehouse · OUT = out of a warehouse",
                                   "IN = vers un entrepôt · OUT = depuis un entrepôt",
                                   "IN = hacia un almacén · OUT = desde un almacén")}>
                      {tip("Direction", "Direction", "Dirección")}
                    </th>
                    <th className="px-3 py-2 text-right"
                        title={tip("Quantity moved",
                                   "Quantité déplacée",
                                   "Cantidad movida")}>
                      {tip("Qty", "Qté", "Cant.")}
                    </th>
                    <th className="px-3 py-2 text-left"
                        title={tip("Readable serial number — joined from Products_By_Serial via SerialID",
                                   "Numéro de série lisible — joint depuis Products_By_Serial via SerialID",
                                   "Número de serie legible — unido desde Products_By_Serial vía SerialID")}>
                      {tip("Serial #", "Nº de série", "Nº de serie")}
                    </th>
                    <th className="px-3 py-2 text-left"
                        title={tip("Source warehouse / supplier",
                                   "Entrepôt source / fournisseur",
                                   "Almacén origen / proveedor")}>
                      {tip("From", "De", "Desde")}
                    </th>
                    <th className="px-3 py-2 text-left"
                        title={tip("Destination warehouse / customer",
                                   "Entrepôt destination / client",
                                   "Almacén destino / cliente")}>
                      {tip("To", "Vers", "Hasta")}
                    </th>
                    <th className="px-3 py-2 text-left">
                      {tip("Handled by", "Traité par", "Manejado por")}
                    </th>
                    <th className="px-3 py-2 text-left">
                      {tip("Notes", "Notes", "Notas")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rowsBusy ? (
                    <tr><td colSpan={pickedMap.size > 1 ? 9 : 8} className="px-3 py-6 text-center text-slate-400">
                      Loading transfers…
                    </td></tr>
                  ) : !Array.isArray(rows) || rows.length === 0 ? (
                    <tr><td colSpan={pickedMap.size > 1 ? 9 : 8} className="px-3 py-6 text-center text-slate-400">
                      {tip(
                        `No transfers recorded for ${pickedMap.size === 1 ? "this part" : "these parts"}${anyFilterActive ? " with these filters" : ""}.`,
                        `Aucun transfert enregistré${anyFilterActive ? " avec ces filtres" : ""}.`,
                        `Sin transferencias registradas${anyFilterActive ? " con estos filtros" : ""}.`)}
                    </td></tr>
                  ) : rows.map((t, idx) => {
                    const isIn  = t?.direction === "IN";
                    const isOut = t?.direction === "OUT";
                    const tsStr = String(t?.ts ?? "").slice(0, 16).replace("T", " ");
                    const partRec = pickedMap.get(t?.part_id);
                    return (
                      <tr key={`${t?.transfer_id || idx}`}
                          className="border-t border-slate-100"
                          data-testid="inventory-transfer-row">
                        <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                          {tsStr || "—"}
                        </td>
                        {pickedMap.size > 1 && (
                          <td className="px-3 py-2 text-xs text-slate-700 truncate max-w-[180px]"
                              title={fmtSku(partRec) || undefined}>
                            {fmtSku(partRec) || "—"}
                          </td>
                        )}
                        <td className="px-3 py-2">
                          {isIn && (
                            <span className="inline-flex items-center gap-1 text-green-700 text-[10px] font-bold uppercase">
                              <ArrowDown size={11} weight="bold" /> In
                            </span>
                          )}
                          {isOut && (
                            <span className="inline-flex items-center gap-1 text-red-700 text-[10px] font-bold uppercase">
                              <ArrowUp size={11} weight="bold" /> Out
                            </span>
                          )}
                          {!isIn && !isOut && (
                            <span className="text-slate-400 text-[10px] font-bold uppercase">
                              {t.direction || "—"}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{t.qty}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {t.serial_number ? (
                            <>
                              {t.serial_number}
                              {t.serial_product && (
                                <div className="text-[10px] text-slate-400 font-sans">
                                  {t.serial_product}
                                </div>
                              )}
                            </>
                          ) : t.serial_key ? (
                            <span className="text-slate-400">{t.serial_key}</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {t.source?.label || t.source?.type || "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {t.destination?.label || t.destination?.type || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {t.actor || "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-500">{t.notes || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {Array.isArray(rows) && rows.length >= 1000 && (
                <p className="text-[11px] text-slate-400 px-3 py-2 italic">
                  Showing the most recent 1,000 transfers (newest first).
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}


function EndpointRow({ side, form, setForm, warehouses }) {
  const typeKey  = `${side}_type`;
  const idKey    = `${side}_id`;
  const labelKey = `${side}_label`;
  const type     = form[typeKey];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end p-2 bg-slate-50 rounded">
      <div>
        <Label className="text-xs capitalize">{side} type</Label>
        <select value={type}
                onChange={(e) => setForm({ ...form, [typeKey]: e.target.value, [idKey]: "", [labelKey]: "" })}
                className="block w-full h-9 px-2 text-sm border border-slate-300 rounded-md bg-white"
                data-testid={`inventory-transfer-${side}-type`}>
          <option value="warehouse">Warehouse</option>
          <option value="supplier">Supplier</option>
          <option value="customer">Customer</option>
        </select>
      </div>
      {type === "warehouse" ? (
        <div className="md:col-span-2">
          <Label className="text-xs">Warehouse</Label>
          <select value={form[idKey]}
                  onChange={(e) => {
                    const wid = e.target.value;
                    const w = warehouses.find((x) => x.id === wid);
                    setForm({ ...form, [idKey]: wid, [labelKey]: w?.name || "" });
                  }}
                  className="block w-full h-9 px-2 text-sm border border-slate-300 rounded-md bg-white"
                  data-testid={`inventory-transfer-${side}-warehouse`}>
            <option value="">Pick a warehouse…</option>
            {(Array.isArray(warehouses) ? warehouses : []).map((w) => (
              <option key={w?.id ?? Math.random()} value={w?.id ?? ""}>{w?.name || "(unnamed)"}</option>
            ))}
          </select>
        </div>
      ) : (
        <>
          <div>
            <Label className="text-xs">{type === "supplier" ? "Supplier name" : "Customer ID"}</Label>
            <Input value={form[idKey]}
                   onChange={(e) => setForm({ ...form, [idKey]: e.target.value })}
                   data-testid={`inventory-transfer-${side}-id`} />
          </div>
          <div>
            <Label className="text-xs">Label</Label>
            <Input value={form[labelKey]}
                   onChange={(e) => setForm({ ...form, [labelKey]: e.target.value })}
                   data-testid={`inventory-transfer-${side}-label`} />
          </div>
        </>
      )}
    </div>
  );
}


// (Tab 4 — Unassigned products — was removed in iter150AZ.98 and
//  folded into the Parts tab; serial-numbered equipment without an
//  Owner_Company now appears alongside regular Parts rows with a
//  separate "Unassigned" status pill.)
